import type { ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';
import { ResourceCatalog } from '@components/ui/catalog/ResourceCatalog';
import type {
  CatalogInteractionHandler,
  CatalogNotifier,
} from '@components/ui/catalog/ResourceCatalog/ResourceCatalog.interactions';
import type {
  CatalogRow,
  CatalogVerbs,
  ResourceCatalogProps,
} from '@components/ui/catalog/ResourceCatalog/ResourceCatalog.types';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';

// Type-only import via the direct /fixtures path — never the feature barrel.
import type { TemplateCatalogBootstrapRow } from '../fixtures/types';
import type { TemplateCatalogEntry } from '../utils/catalogRows';
import { TEMPLATE_CATALOG_LABELS, toCatalogEntries } from '../utils/catalogRows';

const formatLastUsed = (iso: string | null): string => {
  if (!iso) return 'Never used';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Never used';
  return `Last used ${date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })}`;
};

export interface TemplateCatalogProps {
  /** Raw catalog-bootstrap rows; normalized to CatalogRows here. */
  templates: TemplateCatalogBootstrapRow[];
  verbs: CatalogVerbs;
  loading?: boolean;
  /** Fired after archive/restore settle — the tracking seam. */
  onAction?: ResourceCatalogProps['onAction'];
  /**
   * Catalog interaction seam (filter/sort/expansion/undo). Statsig event
   * tracking lands with the route wiring; this component stays presentation-only
   * and Storybook-safe (no provider requirements).
   */
  onInteraction?: CatalogInteractionHandler;
  /** Opens the templates-management surface. Omit to hide the button. */
  onManageTemplates?: () => void;
  /** Verb-outcome toasts via the host's notistack; built-in fallback. */
  notifier?: CatalogNotifier;
}

/**
 * Note-template face of the generic ResourceCatalog (GDN-2482): adapts the
 * catalog-bootstrap rows via toCatalogEntries and renders each row's expandable
 * detail (linked appointment types, owner, recency, and — for public templates a
 * clinician adopted — a Public provenance chip). Presentation-only: the host
 * wires the verbs (stories stub them with local state).
 */
export const TemplateCatalog: React.FC<TemplateCatalogProps> = ({
  templates,
  verbs,
  loading,
  onAction,
  onInteraction,
  onManageTemplates,
  notifier,
}) => {
  const entries = useMemo(() => toCatalogEntries(templates), [templates]);

  const rows = useMemo<CatalogRow[]>(
    () => entries.map((entry) => entry.row),
    [entries]
  );

  const entryById = useMemo<Map<string, TemplateCatalogEntry>>(
    () => new Map(entries.map((entry) => [entry.row.id, entry])),
    [entries]
  );

  const renderDetail = useCallback(
    (row: CatalogRow): ReactNode => {
      const entry = entryById.get(row.id);
      if (!entry) return null;
      return (
        <Box sx={{ px: 1, py: 0.5, maxWidth: 560 }}>
          <Stack spacing={0.5}>
            {entry.isPublicAdoption && (
              <Chip
                label="Public template"
                size="small"
                color="info"
                variant="outlined"
                sx={{ alignSelf: 'flex-start' }}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {entry.linkedTypeNames.length > 0
                ? `Used by: ${entry.linkedTypeNames.join(', ')}`
                : 'Not linked to any appointment type'}
            </Typography>
            {row.ownerLabel && (
              <Typography variant="caption" color="text.secondary">
                Owner: {row.ownerLabel}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {formatLastUsed(row.lastUsedAt)} · {row.usage} uses
            </Typography>
          </Stack>
        </Box>
      );
    },
    [entryById]
  );

  const toolbarAction = useMemo<ReactNode>(
    () =>
      onManageTemplates ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<TuneIcon />}
          onClick={onManageTemplates}
        >
          Manage note templates
        </Button>
      ) : undefined,
    [onManageTemplates]
  );

  return (
    <ResourceCatalog
      rows={rows}
      verbs={verbs}
      labels={TEMPLATE_CATALOG_LABELS}
      renderDetail={renderDetail}
      loading={loading}
      onAction={onAction}
      onInteraction={onInteraction}
      toolbarAction={toolbarAction}
      notifier={notifier}
    />
  );
};
